import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, Wallet, ArrowLeft, TrendingUp, TrendingDown, Receipt } from 'lucide-react';

const fmtINR = (a) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(a || 0);
const fmt = (d) => (!d ? '-' : new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

const CREDIT_TYPES = ['credit', 'funding_credit', 'reversal', 'refund', 'payout_release'];

export default function AdminWalletLedgerPage() {
  const { api } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const fetchWallets = useCallback(async () => {
    try {
      const res = await api.get('/admin/wallets');
      setWallets(res.data.wallets || []);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchWallets(); }, [fetchWallets]);

  const openLedger = async (w) => {
    setSelected(w);
    setLedgerLoading(true);
    try {
      const res = await api.get(`/admin/wallets/${w.merchant_id}/ledger?limit=100`);
      setLedger(res.data);
    } finally {
      setLedgerLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  // Ledger detail view
  if (selected) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => { setSelected(null); setLedger(null); }} data-testid="ledger-back"><ArrowLeft className="mr-2 h-4 w-4" />Back to wallets</Button>
        <div>
          <h1 className="text-2xl font-heading font-bold">{selected.merchant?.business_name || 'Merchant'} — Wallet Ledger</h1>
          <p className="text-muted-foreground text-sm">{selected.merchant?.merchant_id} · {selected.merchant?.email}</p>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">Available</p><p className="text-xl font-bold font-mono text-primary">{fmtINR(selected.available_balance)}</p></CardContent></Card>
          <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">Pending</p><p className="text-xl font-bold font-mono text-yellow-500">{fmtINR(selected.pending_balance)}</p></CardContent></Card>
          <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">Total Credited</p><p className="text-xl font-bold font-mono text-emerald-500">{fmtINR(selected.total_credited)}</p></CardContent></Card>
          <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">Total Debited</p><p className="text-xl font-bold font-mono text-red-500">{fmtINR(selected.total_debited)}</p></CardContent></Card>
        </div>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Receipt className="w-4 h-4" />Ledger Entries {ledger ? `(${ledger.total})` : ''}</CardTitle></CardHeader>
          <CardContent>
            {ledgerLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : !ledger?.entries?.length ? (
              <div className="text-center py-10 text-muted-foreground">No ledger entries</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="text-left py-2 px-3 min-w-[150px]">Time</th>
                      <th className="text-left py-2 px-3">Type</th>
                      <th className="text-right py-2 px-3">Amount</th>
                      <th className="text-right py-2 px-3">Balance After</th>
                      <th className="text-left py-2 px-3 min-w-[220px]">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.entries.map((e) => {
                      const isCredit = CREDIT_TYPES.includes(e.entry_type) || (e.amount || 0) >= 0;
                      return (
                        <tr key={e.id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`ledger-row-${e.id}`}>
                          <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{fmt(e.created_at)}</td>
                          <td className="py-2 px-3"><Badge variant="secondary" className="text-[10px]">{(e.entry_type || '').replace(/_/g, ' ')}</Badge></td>
                          <td className={`py-2 px-3 text-right font-mono font-semibold ${isCredit ? 'text-emerald-500' : 'text-red-500'}`}>
                            <span className="inline-flex items-center gap-1 justify-end">{isCredit ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{isCredit ? '+' : '−'}{fmtINR(Math.abs(e.amount || 0))}</span>
                          </td>
                          <td className="py-2 px-3 text-right font-mono">{fmtINR(e.balance_after)}</td>
                          <td className="py-2 px-3">{e.description}{e.reference_id ? <span className="text-xs text-muted-foreground font-mono ml-1">({e.reference_id})</span> : ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Wallet list
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><Wallet className="w-6 h-6" />Merchant Wallets</h1>
        <p className="text-muted-foreground text-sm">Select a merchant to view its full credit/debit ledger with running balance</p>
      </div>
      <Card>
        <CardContent className="pt-6">
          {wallets.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">No merchant wallets yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-3">Merchant</th>
                    <th className="text-right py-2 px-3">Available</th>
                    <th className="text-right py-2 px-3">Pending</th>
                    <th className="text-right py-2 px-3">Credited</th>
                    <th className="text-right py-2 px-3">Debited</th>
                    <th className="text-right py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {wallets.map((w) => (
                    <tr key={w.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => openLedger(w)} data-testid={`wallet-row-${w.merchant_id}`}>
                      <td className="py-3 px-3"><p className="font-medium">{w.merchant?.business_name || '-'}</p><p className="text-xs text-muted-foreground">{w.merchant?.merchant_id}</p></td>
                      <td className="py-3 px-3 text-right font-mono font-semibold text-primary">{fmtINR(w.available_balance)}</td>
                      <td className="py-3 px-3 text-right font-mono text-yellow-500">{fmtINR(w.pending_balance)}</td>
                      <td className="py-3 px-3 text-right font-mono text-emerald-500">{fmtINR(w.total_credited)}</td>
                      <td className="py-3 px-3 text-right font-mono text-red-500">{fmtINR(w.total_debited)}</td>
                      <td className="py-3 px-3 text-right"><Button size="sm" variant="outline">View Ledger</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
